import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { createMessage } from '../graphql/mutations';
import { onNewMessage } from '../graphql/subscriptions';
import { listMessages, messagesByDate } from '../graphql/queries';
import { refreshSelfData } from './userSelf';

/**
 * Notification service for sending and receiving messages between users
 */
class NotificationService {
  constructor() {
    this.client = generateClient();
    this.subscription = null;
    this.handlers = new Set();
    this.currentUsername = null;
    this.currentUserListening = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the notification service
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized && this.currentUserListening === this.currentUsername) {
      return;
    }

    try {
      // Get current user's username
      try {
        const { username } = await getCurrentUser();
        this.currentUsername = username;
        console.log('Notification service: Initialized for', username);
      } catch (error) {
        console.error('Notification service: Could not get current user', error);
        return;
      }
      
      // Clean up any existing subscription
      this.cleanup();

      // Setup subscription to listen for all messages
      this.registerSubscription(); 
      this.isInitialized = true;
    } catch (error) {
      console.error('Notification service: Initialization failed', error);
    }
  }

  /**
   * Register the subscription
   */
  registerSubscription() {
    if (!this.currentUsername) {
      return;
    }

    try {
      this.subscription = this.client.graphql({
        query: onNewMessage,
        // No filter - we'll filter client-side
      }).subscribe({
        next: (result) => {
          if (!result.data?.onNewMessage) return;
          
          const message = result.data.onNewMessage;
          
          // Client-side filtering based on recipients
          const { recipients, content } = message;
          const isBroadcast = !recipients || recipients.length === 0;
          const isForMe = recipients?.includes(this.currentUsername);
          
          // Only process messages that are broadcasts or specifically for me
          if (isBroadcast || isForMe) {
            console.log('Received notification:', message);
            
            // Check if this is a transaction notification and reload user tree if needed
            if (content) {
              // Enhanced transaction detection - check for various transaction-related keywords
              const isTransactionRelated = content.includes('Transaction:') || 
                                           content.includes('deposit') || 
                                           content.includes('withdraw') || 
                                           content.includes('balance');
              
              if (isTransactionRelated) {
                console.log('Transaction notification received, triggering user tree refresh');
                setTimeout(() => {
                  this._triggerUserTreeRefresh();
                }, 2000);
              }
            }
            
            // Notify all registered handlers
            this.handlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error('Notification handler error:', error);
              }
            });
          }
        },
        error: (error) => {
          console.error('Notification subscription error:', error);
          setTimeout(() => this.registerSubscription(), 5000);
        },
        complete: () => {
          setTimeout(() => this.registerSubscription(), 5000);
        }
      });

      // Track which user we're listening for to detect user changes
      this.currentUserListening = this.currentUsername;
    } catch (error) {
      console.error('Failed to register notification subscription:', error);
    }
  }

  /**
   * Trigger a refresh of the user data
   * @private
   */
  _triggerUserTreeRefresh() {
    try {
      // Directly use the imported refreshSelfData
      refreshSelfData().then(() => {
        console.log('Self data refreshed after transaction notification');
      }).catch(error => {
        console.error('Failed to refresh self data after transaction:', error);
      });
    } catch (error) {
      console.error('Failed to refresh self data:', error);
    }
  }

  /**
   * Register a handler for notifications
   * @param {Function} handler - Callback function for notifications (message) => void
   * @returns {Function} Cleanup function
   */
  onNotification(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Handler must be a function');
    }

    this.handlers.add(handler);
    
    // Initialize if needed
    if (!this.isInitialized) {
      this.initialize().catch(console.error);
    }
    
    // Return cleanup function
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Send a notification to specific recipients
   * @param {Object} options 
   * @param {string} options.content - Message content
   * @param {string|string[]} options.recipients - Username(s) of recipient(s)
   * @returns {Promise<Object>} The created message
   */
  async sendNotification({ content, recipients }) {
    if (!content) {
      throw new Error('Content is required');
    }

    // Make sure we have the current username
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.currentUsername) {
      throw new Error('Not authenticated');
    }

    if (!recipients || (Array.isArray(recipients) && recipients.length === 0)) {
      throw new Error('At least one recipient is required');
    }

    // Convert single recipient to array
    const recipientArray = Array.isArray(recipients) ? recipients : [recipients];

    try {
      const result = await this.client.graphql({
        query: createMessage,
        variables: {
          input: {
            content,
            sender: this.currentUsername,
            recipients: recipientArray,
            type: 'MESSAGE', // Add type field for GSI
            createdAt: new Date().toISOString()
          }
        }
      });
      
      return result.data?.createMessage;
    } catch (error) {
      console.error('Failed to send notification:', error);
      throw error;
    }
  }

  /**
   * Send a notification to all users
   * @param {string} content - Message content
   * @returns {Promise<Object>} The created message
   */
  async broadcast(content) {
    if (!content) {
      throw new Error('Content is required');
    }

    try {
      // Use empty array for broadcast
      const result = await this.client.graphql({
        query: createMessage,
        variables: {
          input: {
            content,
            sender: this.currentUsername || 'system',
            recipients: [], // Empty array for broadcast
            type: 'MESSAGE', // Add type field for GSI
            createdAt: new Date().toISOString()
          }
        }
      });
      
      return result.data?.createMessage;
    } catch (error) {
      console.error('Failed to broadcast notification:', error);
      throw error;
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  /**
   * Fetch message history
   * @param {Object} options
   * @param {number} [options.limit=20] - Maximum number of messages to fetch
   * @param {string} [options.nextToken] - Pagination token for next page
   * @param {boolean} [options.forCurrentUser=true] - If true, only fetch messages for current user
   * @returns {Promise<{messages: Array, nextToken: string}>} Messages and next pagination token
   */
  async getMessageHistory({ limit = 20, nextToken, forCurrentUser = true } = {}) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Try to use the sorted query if it exists (after schema update)
      // This will be available after running: amplify push
      if (messagesByDate) {
        try {
          // Use the new sorted query
          const result = await this.client.graphql({
            query: messagesByDate,
            variables: {
              type: 'MESSAGE', // Constant partition key
              sortDirection: 'DESC', // Newest first
              limit,
              nextToken
            }
          });

          if (!result.data?.messagesByDate) {
            return { messages: [], nextToken: null };
          }

          let messages = result.data.messagesByDate.items;
          
          // Filter for current user if needed
          if (forCurrentUser && this.currentUsername) {
            messages = messages.filter(msg => 
              msg.sender === this.currentUsername || 
              (msg.recipients && msg.recipients.includes(this.currentUsername)) ||
              (!msg.recipients || msg.recipients.length === 0) // Broadcasts
            );
          }

          return {
            messages,
            nextToken: result.data.messagesByDate.nextToken
          };
        } catch (error) {
          // If messagesByDate fails, fall back to listMessages
          console.log('messagesByDate not available yet, using listMessages with client-side sorting');
        }
      }
      
      // Fallback to old method with client-side sorting
      // Build filter based on options
      let filter = null;
      
      if (forCurrentUser && this.currentUsername) {
        // Filter for messages where current user is sender or recipient
        filter = {
          or: [
            { sender: { eq: this.currentUsername } },
            { recipients: { contains: this.currentUsername } }
          ]
        };
      }

      const result = await this.client.graphql({
        query: listMessages,
        variables: {
          limit,
          nextToken,
          filter
          // Note: sortDirection and sortField are not supported by listMessages
        }
      });

      if (!result.data?.listMessages) {
        return { messages: [], nextToken: null };
      }

      // Sort messages by createdAt in memory to ensure proper ordering
      const sortedMessages = result.data.listMessages.items.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      return {
        messages: sortedMessages,
        nextToken: result.data.listMessages.nextToken
      };
    } catch (error) {
      console.error('Failed to fetch message history:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Initialize service when module is loaded
notificationService.initialize(); 